import React, { useEffect, useCallback } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { docIcon, renderCardIcon } from "../common/IconLibrary";

const customNodeStyle = `
  bg-gray-800 bg-opacity-50
  border border-gray-600
  rounded-lg shadow-lg
  backdrop-filter backdrop-blur-sm
  text-gray-100
  p-3
  transition-all duration-300 ease-in-out
  hover:bg-opacity-70 hover:shadow-xl
`;

const customEdgeStyle = {
  stroke: '#93C5FD', // blue-300 in Tailwind
  strokeWidth: 2,
  transition: '0.3s ease-in-out',
};

const CustomNode = ({ id, data }) => {
  let targetPosition, sourcePosition;
  const rotation = data?.rotation || 0;
  let leftStyle = { }
  let rightStyle = { }
  switch (rotation) {
    case 90:
      targetPosition = Position.Right;
      sourcePosition = Position.Left;
      leftStyle = { top: "0", bottom: "auto", left: "auto", right: "0" }
      rightStyle = { top: "auto", bottom: "0", left: "auto", right: "0" }
      break;
    case 180:
      targetPosition = Position.Bottom;
      sourcePosition = Position.Top;
      leftStyle = { top: "auto", bottom: "0", left: "auto", right: "0" }
      rightStyle = { top: "auto", bottom: "0", left: "0", right: "auto" }
      break;
    case 270:
      targetPosition = Position.Bottom;
      sourcePosition = Position.Top;
      leftStyle = { top: "auto", bottom: "auto", left: "0", right: "auto" }
      rightStyle = { top: "auto", bottom: "auto", left: "auto", right: "0" }
      break;
    default:
      targetPosition = Position.Left;
      sourcePosition = Position.Right;
  }
  return (
    <div className={`flex items-center justify-center`}>
      <div
        className={`px-4 py-2 text-gray-200 text-xs ${id.indexOf("event") === -1 ? customNodeStyle : customEdgeStyle}`}
        style={{
          transform: `rotate(${rotation}deg)`,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Handle
          type="target"
          position={targetPosition}
          style={leftStyle}
        />
        <div>
          {data.label}
        </div>
        <Handle
          type="source"
          position={sourcePosition}
          style={rightStyle}
        />
      </div>
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

const FlowDiagram = ({ title, description, nodes, edges, onClose }) => {
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]); // Only re-run if handleKeyDown changes

  return (
    <div className="flex flex-col h-[90vh] w-full">
      <div className="flex justify-between items-center p-4">
        <span className="flex items-center text-gray-200 text-2xl font-sol-w01">
          <span className="flex-shrink-0 mr-0">{renderCardIcon(docIcon)}</span>
          <span className="flex-grow">{title}</span>
        </span>
        <button onClick={onClose} className="text-white hover:text-gray-300">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      {description && (
        <div className="px-4 mb-4 text-gray-300">
          {description}
        </div>
      )}
      <div className="flex-grow">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          className="h-full"
        >
          <Background color="#4B5563" gap={16} />
          <Controls className="bg-gray-800 text-white" />
        </ReactFlow>
      </div>
    </div>
  );
};

export default FlowDiagram;